import { Component, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { CheckboxComponent, InputComponent } from '@libs/ui-lib';
import { EMAIL_REGEX_PATTERN, phoneNumberValidator, toE164PhoneNumber } from '@libs/utils';
import { AsYouType } from 'libphonenumber-js';
import { SiteSetupNavbarComponent } from '../site-setup/components/site-setup-navbar/site-setup-navbar.component';
import { SiteSetupComponentService, StepNumberEnum } from '../site-setup/site-setup.component.service';

// Pre-fill the country code so users only type national digits, matching the license-agreement signer form.
const DEFAULT_PHONE_VALUE = '+1 ';

@Component({
  selector: 'lib-it-provider-details',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SiteSetupNavbarComponent,
    TranslatePipe,
    SvgIconComponent,
    InputComponent,
    CheckboxComponent,
  ],
  templateUrl: './it-provider-details.component.html',
  styleUrl: './it-provider-details.component.scss',
})
export class ItProviderDetailsComponent {
  private readonly service = inject(SiteSetupComponentService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly vm = this.service.viewModel;
  protected readonly StepNumberEnum = StepNumberEnum;
  protected isSubmitted = false;

  protected readonly form = new FormGroup({
    itProviderName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    itPrimaryContactFirstName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    itPrimaryContactLastName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    itPrimaryContactEmail: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(EMAIL_REGEX_PATTERN)],
    }),
    itPrimaryContactPhoneNumber: new FormControl(DEFAULT_PHONE_VALUE, {
      nonNullable: true,
      validators: [Validators.required, phoneNumberValidator()],
    }),
    noItProvider: new FormControl(false, { nonNullable: true }),
  });

  constructor() {
    // "I don't have an IT provider" toggles off the provider-field requirements.
    this.form.controls.noItProvider.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((noProvider) => this.setProviderFieldsRequired(!noProvider));

    this.prefillFromDraft();
  }

  private setProviderFieldsRequired(required: boolean): void {
    const controls = this.form.controls;
    controls.itProviderName.setValidators(required ? [Validators.required] : []);
    controls.itPrimaryContactFirstName.setValidators(required ? [Validators.required] : []);
    controls.itPrimaryContactLastName.setValidators(required ? [Validators.required] : []);
    controls.itPrimaryContactEmail.setValidators(required ? [Validators.required, Validators.pattern(EMAIL_REGEX_PATTERN)] : []);
    controls.itPrimaryContactPhoneNumber.setValidators(required ? [Validators.required, phoneNumberValidator()] : []);

    for (const control of [
      controls.itProviderName,
      controls.itPrimaryContactFirstName,
      controls.itPrimaryContactLastName,
      controls.itPrimaryContactEmail,
      controls.itPrimaryContactPhoneNumber,
    ]) {
      control.updateValueAndValidity({ emitEvent: false });
    }
  }

  // Prefill from a previously-entered draft so navigating back from install options doesn't lose input.
  private prefillFromDraft(): void {
    const draft = this.vm().itProviderDraft;
    const hasDraft =
      draft.itProviderName ||
      draft.itPrimaryContactFirstName ||
      draft.itPrimaryContactLastName ||
      draft.itPrimaryContactEmail ||
      draft.itPrimaryContactPhoneNumber;
    if (!hasDraft) {
      return;
    }

    this.form.patchValue({
      itProviderName: draft.itProviderName ?? '',
      itPrimaryContactFirstName: draft.itPrimaryContactFirstName ?? '',
      itPrimaryContactLastName: draft.itPrimaryContactLastName ?? '',
      itPrimaryContactEmail: draft.itPrimaryContactEmail ?? '',
      itPrimaryContactPhoneNumber: draft.itPrimaryContactPhoneNumber
        ? new AsYouType('US').input(draft.itPrimaryContactPhoneNumber)
        : DEFAULT_PHONE_VALUE,
    });
  }

  // Show a field error only after submit, and only when a provider is being entered.
  protected hasError(control: AbstractControl): boolean {
    return this.isSubmitted && !this.form.controls.noItProvider.value && control.invalid;
  }

  protected next(): void {
    this.isSubmitted = true;

    // No IT provider -> treat as self install and continue the existing flow.
    if (this.form.controls.noItProvider.value) {
      this.service.chooseSelfInstall();
      return;
    }

    if (this.form.invalid) {
      return;
    }

    const value = this.form.getRawValue();
    this.service.continueToInstallOptions({
      itProviderName: value.itProviderName,
      itPrimaryContactFirstName: value.itPrimaryContactFirstName,
      itPrimaryContactLastName: value.itPrimaryContactLastName,
      itPrimaryContactEmail: value.itPrimaryContactEmail,
      itPrimaryContactPhoneNumber: toE164PhoneNumber(value.itPrimaryContactPhoneNumber),
    });
  }

  // Reformats on every keystroke (same behavior as the signer / FFD Notes registration phone field).
  protected onPhoneInput(raw: string): void {
    const digits = raw.replaceAll(/(?!^\+)\D/g, '');
    const formatted = new AsYouType('US').input(digits);
    this.form.controls.itPrimaryContactPhoneNumber.setValue(formatted);
  }
}
